import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, AutoAwesome as AutoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect, { type SelectOption } from '../SearchableSelect';
import loteService, { LoteAsignacion, ProductoLote } from '../../services/loteService';
import LoteAsistenteDialog from './LoteAsistenteDialog';

export interface LoteAsignacionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (lotes: LoteAsignacion[]) => void;
  codigoArticulo: string;
  cantidad: number;
  nroUbicacion: number;
  mode: 'ingreso' | 'egreso' | 'transferencia';
  esSerie?: boolean;
  requiereVencimiento?: boolean;
  initialLotes?: LoteAsignacion[];
  /** Solo consulta: muestra lotes sin editar. */
  readOnly?: boolean;
  /** Si se informa, solo se pueden elegir estos lotes (p. ej. facturados en NC). */
  lotesPermitidos?: LoteAsignacion[] | null;
}

const emptyRow = (): LoteAsignacion => ({
  idLote: null,
  nroLote: '',
  fechaVencimiento: null,
  fechaFabricacion: null,
  cantidad: 1,
});

const LoteAsignacionDialog: React.FC<LoteAsignacionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  codigoArticulo,
  cantidad,
  nroUbicacion,
  mode,
  esSerie = false,
  requiereVencimiento = false,
  initialLotes,
  readOnly = false,
  lotesPermitidos,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<LoteAsignacion[]>([]);
  const [existentes, setExistentes] = useState<ProductoLote[]>([]);
  const [asistenteOpen, setAsistenteOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);

  const suma = useMemo(() => rows.reduce((a, r) => a + (Number(r.cantidad) || 0), 0), [rows]);
  const restringido = (lotesPermitidos?.length ?? 0) > 0;
  const isEgreso = mode === 'egreso' || mode === 'transferencia';
  const usarSelector = isEgreso || restringido;

  const loteOptions = useMemo<SelectOption<number>[]>(
    () =>
      existentes.map((e) => ({
        value: e.idLote,
        label: [
          e.nroLote,
          e.fechaVencimiento ? `(vto ${String(e.fechaVencimiento).slice(0, 10)})` : null,
          `— ${e.cantidadTotal}`,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    [existentes],
  );

  useEffect(() => {
    if (!open) return;
    setRows(
      initialLotes && initialLotes.length > 0
        ? initialLotes.map((l) => ({ ...l }))
        : [emptyRow()],
    );

    if (readOnly) {
      setExistentes([]);
      setLoadingSuggest(false);
      return;
    }

    if (restringido) {
      const permitidos = (lotesPermitidos ?? [])
        .filter((l) => l.idLote != null && Number(l.idLote) > 0)
        .map(
          (l): ProductoLote => ({
            idLote: Number(l.idLote),
            codigoArticulo,
            nroLote: String(l.nroLote ?? '').trim(),
            esSerie: false,
            fechaFabricacion: l.fechaFabricacion ?? null,
            fechaVencimiento: l.fechaVencimiento ?? null,
            estado: 'A',
            fechaAlta: '',
            cantidadTotal: Number(l.cantidad) || 0,
            depositos: [],
            observaciones: l.observaciones ?? null,
          }),
        );
      setExistentes(permitidos);
      setLoadingSuggest(false);
      return;
    }

    loteService
      .search({ codigoArticulo, nroUbicacion: isEgreso ? nroUbicacion : undefined, soloConSaldo: isEgreso, pageSize: 100 })
      .then((r) => setExistentes(r.items))
      .catch(() => setExistentes([]));

    if (isEgreso && (!initialLotes || initialLotes.length === 0) && cantidad > 0) {
      setLoadingSuggest(true);
      loteService
        .sugerir(codigoArticulo, nroUbicacion, cantidad)
        .then((sug) => {
          if (sug.length > 0) {
            setRows(
              sug.map((s) => ({
                idLote: s.idLote,
                nroLote: s.nroLote,
                fechaVencimiento: s.fechaVencimiento,
                cantidad: s.cantidadSugerida,
              })),
            );
          }
        })
        .catch(() => undefined)
        .finally(() => setLoadingSuggest(false));
    }
  }, [
    open,
    codigoArticulo,
    nroUbicacion,
    cantidad,
    mode,
    isEgreso,
    initialLotes,
    readOnly,
    restringido,
    lotesPermitidos,
  ]);

  const updateRow = (index: number, patch: Partial<LoteAsignacion>) => {
    if (readOnly) return;
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSelectExistente = (index: number, idLote: number) => {
    const found = existentes.find((e) => e.idLote === idLote);
    if (!found) return;
    updateRow(index, {
      idLote: found.idLote,
      nroLote: found.nroLote,
      fechaVencimiento: found.fechaVencimiento,
      fechaFabricacion: found.fechaFabricacion,
    });
  };

  const handleConfirm = () => {
    if (readOnly) {
      onClose();
      return;
    }
    if (Math.abs(suma - cantidad) > 0.0005) {
      enqueueSnackbar(`La suma de lotes (${suma}) debe igualar la cantidad (${cantidad})`, {
        variant: 'warning',
      });
      return;
    }
    for (const r of rows) {
      if (!r.idLote && !r.nroLote?.trim()) {
        enqueueSnackbar('Cada fila debe tener número de lote/serie', { variant: 'warning' });
        return;
      }
      if ((isEgreso || restringido) && !r.idLote) {
        enqueueSnackbar(
          restringido
            ? 'Debe seleccionar un lote de los facturados'
            : 'En egresos debe seleccionar lotes existentes',
          { variant: 'warning' },
        );
        return;
      }
      if (requiereVencimiento && !r.idLote && !r.fechaVencimiento) {
        enqueueSnackbar('La fecha de vencimiento es obligatoria', { variant: 'warning' });
        return;
      }
      if (esSerie && Number(r.cantidad) !== 1) {
        enqueueSnackbar('Las series solo admiten cantidad 1', { variant: 'warning' });
        return;
      }
    }
    onConfirm(rows);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {readOnly ? 'Lotes' : 'Asignar'} {esSerie ? 'series' : 'lotes'} — {codigoArticulo}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Cantidad requerida: {cantidad} · Asignado: {suma}
              {loadingSuggest ? ' · sugiriendo FEFO…' : ''}
            </Typography>
            {!readOnly && (
              <Stack direction="row" spacing={1}>
                {!usarSelector && (
                  <Button size="small" startIcon={<AutoIcon />} onClick={() => setAsistenteOpen(true)}>
                    Asistente
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setRows((prev) => [...prev, emptyRow()])}
                >
                  Agregar
                </Button>
              </Stack>
            )}
          </Stack>
          {restringido && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Solo lotes incluidos en la factura origen
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lote / Serie</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell width={120}>Cantidad</TableCell>
                {!readOnly && <TableCell width={56} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">
                        {row.nroLote || '—'}
                        {row.fechaVencimiento
                          ? ` (vto ${String(row.fechaVencimiento).slice(0, 10)})`
                          : ''}
                      </Typography>
                    ) : usarSelector ? (
                      <SearchableSelect<number>
                        size="small"
                        value={row.idLote ?? null}
                        onChange={(idLote) => {
                          if (idLote == null) {
                            updateRow(index, {
                              idLote: null,
                              nroLote: '',
                              fechaVencimiento: null,
                              fechaFabricacion: null,
                            });
                            return;
                          }
                          handleSelectExistente(index, idLote);
                        }}
                        options={loteOptions}
                        placeholder={restringido ? 'Lote facturado…' : 'Buscar lote / serie…'}
                        allowEmpty
                        emptyOptionLabel="Seleccionar…"
                        noOptionsText={restringido ? 'Sin lotes en la factura' : 'Sin lotes'}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={row.nroLote ?? ''}
                        onChange={(e) =>
                          updateRow(index, { nroLote: e.target.value.toUpperCase(), idLote: null })
                        }
                        placeholder="Nro lote"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">
                        {row.fechaVencimiento ? String(row.fechaVencimiento).slice(0, 10) : '—'}
                      </Typography>
                    ) : (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={row.fechaVencimiento ? String(row.fechaVencimiento).slice(0, 10) : ''}
                        onChange={(e) =>
                          updateRow(index, { fechaVencimiento: e.target.value || null })
                        }
                        disabled={!!row.idLote && usarSelector}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">{row.cantidad}</Typography>
                    ) : (
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={row.cantidad}
                        onChange={(e) => updateRow(index, { cantidad: Number(e.target.value) || 0 })}
                        inputProps={{ min: esSerie ? 1 : 0.001, step: esSerie ? 1 : 0.001 }}
                        disabled={esSerie}
                      />
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                        disabled={rows.length <= 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!readOnly && !usarSelector && existentes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Lotes existentes del artículo (opcionales para reutilizar):{' '}
                {existentes
                  .slice(0, 8)
                  .map((e) => e.nroLote)
                  .join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {readOnly ? (
            <Button variant="contained" onClick={onClose}>
              Cerrar
            </Button>
          ) : (
            <>
              <Button onClick={onClose}>Cancelar</Button>
              <Button variant="contained" onClick={handleConfirm}>
                Confirmar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <LoteAsistenteDialog
        open={asistenteOpen}
        onClose={() => setAsistenteOpen(false)}
        codigoArticulo={codigoArticulo}
        esSerie={esSerie}
        onGenerated={(nums) => {
          if (esSerie) {
            setRows(nums.map((n) => ({ ...emptyRow(), nroLote: n, cantidad: 1 })));
          } else if (nums[0]) {
            setRows([{ ...emptyRow(), nroLote: nums[0], cantidad }]);
          }
        }}
      />
    </>
  );
};

export default LoteAsignacionDialog;
